/**
 * What the two of them say before the bell, decided by the state of the fight.
 *
 * **No dice anywhere in this module.** A line is chosen by what is true — how
 * many times these two have met and who won, what the player has been doing to
 * the crowd, whether they are on a run, how far off the card they are — and the
 * same state says the same thing every time. That is the whole point of it: a
 * fighter the player has beaten twice has something different to say from one
 * who has never met them, and a random line off a list would throw that away
 * while looking like it was working.
 *
 * The lines are data (`trashTalk.json`) and the rules are order. The first
 * entry whose conditions all hold is the one that gets said, so the file reads
 * top-down from the most specific thing that could be true to the least, and
 * each speaker ends with an unconditional line that is always available.
 *
 * **The arithmetic is `lines.js`'s**, which is this module with the trash talk
 * taken out of it. It was extracted when Sala arrived (step 62) and needed the
 * same rule keyed on different facts; what stayed here is what this script *is*
 * — its facts, its two speakers, and the state they are read out of.
 *
 * Pure, and it holds no DOM: `src/ui` renders what this returns.
 */

import TALK from './trashTalk.json';
import { matches as matchesLine, pickLine as pick, validateLines } from './lines.js';

export class TrashTalkConfigError extends Error {}

/** Who can speak. */
export const SPEAKERS = Object.freeze(['opponent', 'player']);

/**
 * Every fact a line may be keyed on, and how it is matched.
 *
 * `number` takes `{ min, max, is }`, `flag` takes a boolean, `text` takes a
 * string or `{ is }`, and `list` takes `{ has }` or `{ lacks }`. A `when` key
 * that is not in here throws at boot, because the only thing it can be is a
 * typo — and a typo in a condition is a line that silently never fires.
 */
export const FACTS = Object.freeze({
  /** How many times these two have fought, ever. */
  met: 'number',
  /** Of those, how many the player won. */
  beaten: 'number',
  /** And how many the opponent won. */
  lost: 'number',
  /** The player's run: positive for wins, negative for losses. */
  streak: 'number',
  /** Places the player is off the contender threshold. Zero means they are in. */
  gap: 'number',
  /** The player's rank, and the opponent's. */
  rank: 'number',
  theirRank: 'number',
  /** Fights the player has had. Zero is their first night. */
  fights: 'number',
  /** Whether this one is for the card. */
  act: 'flag',
  /** Whether the player is inside the threshold. */
  contender: 'flag',
  /** The tier the opponent fights at. */
  tier: 'text',
  /** The opponent's primary style. */
  style: 'text',
  /** The heat events the player earned last round, as types. */
  heat: 'list',
  /**
   * Whose room it is: `take` when the opponent holds the venue they are
   * fighting in, `defend` when the player does, `none` otherwise.
   */
  stake: 'text',
});

/**
 * The state two fighters walk out in.
 *
 * Read off the campaign and the ladder rather than passed around, so a line
 * cannot be keyed on something nobody can produce.
 */
export function readFacts({ campaign, opponent, act = false }) {
  const head = campaign.against(opponent.id);
  const gate = campaign.gate;
  const last = campaign.history[0] ?? null;
  return Object.freeze({
    met: head.met,
    beaten: head.wins,
    lost: head.losses,
    streak: campaign.streak,
    gap: gate.short,
    rank: gate.rank,
    theirRank: opponent.rank ?? 0,
    fights: campaign.fights,
    act: Boolean(act),
    contender: gate.open,
    tier: opponent.tier ?? '',
    style: opponent.styles?.[0] ?? '',
    heat: (last?.heat ?? []).map((event) => event.type ?? event),
    stake: stakeOf(campaign, opponent),
  });
}

/**
 * Whose room tonight's fight is in, read off the booked card's venue and the
 * ladder's holder **now** — the card is made once and held (gotcha 45), and a
 * room can change hands across the city while it sits on the table.
 */
function stakeOf(campaign, opponent) {
  const venue = campaign.next?.opponent?.id === opponent.id ? campaign.next.venue : null;
  if (!venue) return 'none';
  const holder = campaign.ladder?.venue?.(venue.id)?.holder ?? venue.holder;
  if (holder === opponent.id) return 'take';
  if (holder === campaign.ladder?.playerId) return 'defend';
  return 'none';
}

/** Whether every condition on a line holds. A line with none always does. */
export function matches(line, facts) {
  return matchesLine(line, facts, FACTS);
}

/**
 * The line this speaker says, given the state. Never null: the file guarantees
 * an unconditional line for each of them, and the validator enforces it.
 */
export function pickLine(facts, { speaker = 'opponent', config = TALK } = {}) {
  return pick(config.lines, facts, { voice: speaker, table: FACTS });
}

/** Both sides of it, for a screen that shows the pair. */
export function exchange(facts, { config = TALK } = {}) {
  return Object.freeze({
    facts,
    opponent: pickLine(facts, { speaker: 'opponent', config }),
    player: pickLine(facts, { speaker: 'player', config }),
  });
}

export function validateTrashTalk(config = TALK) {
  return validateLines(config, {
    facts: FACTS,
    voices: SPEAKERS,
    what: 'trash talk',
    Err: TrashTalkConfigError,
  });
}

// A file that cannot always answer fails at boot rather than at a blank screen.
validateTrashTalk(TALK);

export { TALK };
